import type { DiscoveredFile, SideEvent, TurnEvent } from "@tktuner/core";

/**
 * Known non-conversational line types. They carry no usage; several have no
 * uuid or timestamp. Skipped silently (unlike genuinely unknown types, which
 * are counted so the parser can evolve with the log format).
 */
const SIDECAR_TYPES = new Set([
  "ai-title",
  "mode",
  "permission-mode",
  "last-prompt",
  "agent-name",
  "file-history-snapshot",
  "queue-operation",
  "queued-command",
  "started",
  "result",
  "frame-link",
  "summary",
]);

export interface LineOutcome {
  turn?: TurnEvent;
  event?: SideEvent;
  unknownType?: string;
  parseError?: boolean;
}

/**
 * Parses one JSONL line into a normalized outcome. Defensive by design: any
 * malformed line degrades to a counter, never a crash. Privacy invariant:
 * message text, thinking content and tool inputs are never extracted — only
 * numeric lengths, tool names, usage numbers and identifiers.
 */
export function parseLine(raw: string, file: DiscoveredFile): LineOutcome {
  const text = raw.trim();
  if (text === "") return {};
  let obj: Record<string, unknown>;
  try {
    const parsed: unknown = JSON.parse(text);
    if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
      return { parseError: true };
    }
    obj = parsed as Record<string, unknown>;
  } catch {
    return { parseError: true };
  }
  const type = obj.type;
  if (typeof type !== "string") return { parseError: true };

  switch (type) {
    case "assistant":
      return assistantToTurn(obj, file);
    case "user":
      return userToEvent(obj, file);
    case "system":
      return systemToEvent(obj, file);
    case "attachment":
      return attachmentToEvent(obj, file);
    default:
      return SIDECAR_TYPES.has(type) ? {} : { unknownType: type };
  }
}

function assistantToTurn(obj: Record<string, unknown>, file: DiscoveredFile): LineOutcome {
  const uuid = str(obj.uuid);
  const msg = obj.message;
  if (!uuid || typeof msg !== "object" || msg === null) return { parseError: true };
  const message = msg as Record<string, unknown>;
  const usage = asRecord(message.usage);
  const cacheCreation = asRecord(usage.cache_creation);
  const write5m = num(cacheCreation.ephemeral_5m_input_tokens);
  const write1h = num(cacheCreation.ephemeral_1h_input_tokens);
  const cacheWrite =
    usage.cache_creation_input_tokens !== undefined
      ? num(usage.cache_creation_input_tokens)
      : write5m + write1h;

  const tools: string[] = [];
  let thinkingChars = 0;
  let textChars = 0;
  const content = Array.isArray(message.content) ? message.content : [];
  for (const rawBlock of content) {
    if (typeof rawBlock !== "object" || rawBlock === null) continue;
    const block = rawBlock as Record<string, unknown>;
    if (block.type === "tool_use") {
      const name = str(block.name);
      if (name) tools.push(name);
    } else if (block.type === "thinking") {
      thinkingChars += typeof block.thinking === "string" ? block.thinking.length : 0;
    } else if (block.type === "text") {
      textChars += typeof block.text === "string" ? block.text.length : 0;
    }
  }

  const turn: TurnEvent = {
    agent: "claude-code",
    uuid,
    messageId: str(message.id),
    requestId: str(obj.requestId),
    sessionId: str(obj.sessionId) ?? file.sessionId ?? "unknown",
    parentSessionId: file.parentSessionId,
    agentId: str(obj.agentId) ?? file.agentId,
    isSidechain: obj.isSidechain === true,
    projectPath: str(obj.cwd) ?? "",
    slug: str(obj.slug),
    timestamp: parseTs(obj.timestamp) ?? 0,
    model: str(message.model) ?? "unknown",
    usage: {
      input: num(usage.input_tokens),
      output: num(usage.output_tokens),
      cacheRead: num(usage.cache_read_input_tokens),
      cacheWrite,
      cacheWrite5m: write5m,
      cacheWrite1h: write1h,
    },
    tools,
    numToolUses: tools.length,
    thinkingChars,
    textChars,
    stopReason: str(message.stop_reason),
    gitBranch: str(obj.gitBranch),
    cliVersion: str(obj.version),
    speed: str(usage.speed),
    inferenceGeo: str(usage.inference_geo),
    filePath: file.path,
  };
  return { turn };
}

/**
 * User lines never carry usage. The only signal extracted is the Agent-tool
 * result summary (`toolUseResult`), whitelisted field by field — its `prompt`
 * and `content` fields are content and must never be stored.
 */
function userToEvent(obj: Record<string, unknown>, file: DiscoveredFile): LineOutcome {
  const tur = obj.toolUseResult;
  if (typeof tur !== "object" || tur === null || Array.isArray(tur)) return {};
  const r = tur as Record<string, unknown>;
  const agentId = str(r.agentId);
  const looksLikeAgentResult =
    agentId !== undefined && ("resolvedModel" in r || "agentType" in r || "totalTokens" in r);
  if (!looksLikeAgentResult) return {};
  return {
    event: {
      kind: "agent_result",
      sessionId: str(obj.sessionId) ?? file.sessionId ?? "unknown",
      timestamp: parseTs(obj.timestamp),
      filePath: file.path,
      data: compact({
        agentId,
        agentType: str(r.agentType),
        resolvedModel: str(r.resolvedModel),
        status: str(r.status),
        totalDurationMs: numOrU(r.totalDurationMs),
        totalTokens: numOrU(r.totalTokens),
        totalToolUseCount: numOrU(r.totalToolUseCount),
      }),
    },
  };
}

function systemToEvent(obj: Record<string, unknown>, file: DiscoveredFile): LineOutcome {
  const subtype = str(obj.subtype);
  const sessionId = str(obj.sessionId) ?? file.sessionId ?? "unknown";
  const timestamp = parseTs(obj.timestamp);
  if (subtype === "compact_boundary") {
    const meta = asRecord(obj.compactMetadata);
    return {
      event: {
        kind: "compact_boundary",
        sessionId,
        timestamp,
        filePath: file.path,
        data: compact({
          trigger: str(meta.trigger),
          preTokens: numOrU(meta.preTokens),
          postTokens: numOrU(meta.postTokens),
          cumulativeDroppedTokens: numOrU(meta.cumulativeDroppedTokens),
          durationMs: numOrU(meta.durationMs),
        }),
      },
    };
  }
  if (subtype === "turn_duration") {
    return {
      event: {
        kind: "turn_duration",
        sessionId,
        timestamp,
        filePath: file.path,
        data: compact({
          durationMs: numOrU(obj.durationMs),
          messageCount: numOrU(obj.messageCount),
        }),
      },
    };
  }
  return {};
}

function attachmentToEvent(obj: Record<string, unknown>, file: DiscoveredFile): LineOutcome {
  const att = asRecord(obj.attachment);
  const attType = str(att.type);
  if (attType !== "plan_mode" && attType !== "plan_mode_reentry" && attType !== "plan_mode_exit") {
    return {};
  }
  return {
    event: {
      kind: attType === "plan_mode_exit" ? "plan_mode_exit" : "plan_mode",
      sessionId: str(obj.sessionId) ?? file.sessionId ?? "unknown",
      timestamp: parseTs(obj.timestamp),
      filePath: file.path,
      data: {},
    },
  };
}

function str(v: unknown): string | undefined {
  return typeof v === "string" ? v : undefined;
}

function num(v: unknown): number {
  return typeof v === "number" && Number.isFinite(v) ? v : 0;
}

function numOrU(v: unknown): number | undefined {
  return typeof v === "number" && Number.isFinite(v) ? v : undefined;
}

function parseTs(v: unknown): number | undefined {
  if (typeof v !== "string") return undefined;
  const ts = Date.parse(v);
  return Number.isFinite(ts) ? ts : undefined;
}

function asRecord(v: unknown): Record<string, unknown> {
  return typeof v === "object" && v !== null && !Array.isArray(v)
    ? (v as Record<string, unknown>)
    : {};
}

function compact(data: Record<string, unknown>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(data)) if (v !== undefined) out[k] = v;
  return out;
}
